/**
 * TermsBooklet — the standing Terms a Schedule incorporates, shown so that clause 3.3 is
 * true.
 *
 * Clause 3.3 of every Schedule says: "A copy of those Terms, in the version and with the
 * checksum identified in clause 3.1, was shown to and accepted by the Driver in the Primelane
 * application before this Schedule was signed." Until this step existed the app asserted that
 * in a signed instrument and never did it. This is the doing.
 *
 * ── THE GATE IS TIME PLUS REACHING THE END, NOT PAGES ─────────────────────────
 * `DocumentReader`'s first gate — turn to the last page — is the right one for a Schedule,
 * which paginates. It is worthless here: the `:::pagebreak:::` fences were taken out of all
 * three Terms bodies on 2026-08-13 and no v1 body carries one, so a booklet is exactly one
 * page and "reached the last page" is satisfied by arriving. So the booklet counts SECONDS,
 * on the same 45-second floor and the same visibility rule the Schedule reader uses, and
 * watches for the END OF THE TEXT scrolling into view. Neither gate claims the Terms were
 * understood; together they are the smallest honest evidence they were in front of the
 * signer, which is the claim clause 3.3 actually makes.
 *
 * ── PROGRESS IS NOT PERSISTED, ON PURPOSE ─────────────────────────────────────
 * `CeremonyDraft` carries the Schedule reader's page, seconds and ticks; it carries nothing
 * of this step's. A reload therefore sends the signer back through the booklet from the top.
 * That is the correct behaviour for a "was shown" gate — the alternative is a reload that
 * restores a satisfied gate for a document this session never displayed.
 *
 * ── THE PDF IS NEVER THE THING THAT BLOCKS ────────────────────────────────────
 * `agreement_terms.storage_path` names a filed PDF that only exists once an admin has run
 * `generate-agreement`'s `render_terms` for that version, per environment — and no
 * environment has (`agreement-terms` holds zero objects on staging today). So the download
 * is offered ONLY after a HEAD says the object is really there. What the signer reads and
 * accepts is the booklet body, which comes from the same row the digest was checked against.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, FileText, Loader2, Lock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Eyebrow, StepHeading } from '@/components/agreements/CeremonyPieces';
import { sanitizeAgreementHtml } from '@/lib/sanitizeAgreementHtml';

interface Props {
    /** The booklet's name — the resolved row's title, or the citation's, or the form code. */
    title: string;
    /** "version 1 · form PL-HP-TERMS · checksum begins ea1557e6fdb89538". */
    citationLabel: string;
    /** What the Schedule calls the signer: "Driver" on the hire purchase, "Borrower" on an advance. */
    partyNoun: string;
    /** One HTML string per page, or null when the booklet could not be resolved. */
    pages: string[] | null;
    /** True while the row is still in flight. */
    loading: boolean;
    readSeconds: number;
    onReadSecondsChange: (seconds: number) => void;
    secondsFloor: number;
    reachedEnd: boolean;
    onReachedEnd: () => void;
    /** The public address of the filed PDF, or null when the row did not resolve. */
    pdfUrl: string | null;
}

export default function TermsBooklet({
    title,
    citationLabel,
    partyNoun,
    pages,
    loading,
    readSeconds,
    onReadSecondsChange,
    secondsFloor,
    reachedEnd,
    onReachedEnd,
    pdfUrl,
}: Props) {
    const [tick, setTick] = useState(readSeconds);
    const tickRef = useRef(readSeconds);
    const onSecondsRef = useRef(onReadSecondsChange);
    useEffect(() => {
        onSecondsRef.current = onReadSecondsChange;
    }, [onReadSecondsChange]);

    const hasBody = Array.isArray(pages) && pages.length > 0;

    // The Schedule reader's own interval, verbatim in behaviour: one timer for the life of
    // the step, stopped at the floor, and skipping any tick where the tab is hidden — a
    // booklet left open in a background tab does not read itself.
    useEffect(() => {
        if (!hasBody) return;
        const timer = window.setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            if (tickRef.current >= secondsFloor) return;
            tickRef.current += 1;
            setTick(tickRef.current);
            onSecondsRef.current(tickRef.current);
        }, 1000);
        return () => window.clearInterval(timer);
    }, [secondsFloor, hasBody]);

    // ── Reaching the end ──────────────────────────────────────────────────────
    // A sentinel after the last page, watched by an IntersectionObserver. The booklet scrolls
    // with the window rather than inside a box: forty screens of standing terms read badly
    // through a phone-sized porthole, and the ceremony's footer is already sticky.
    const endRef = useRef<HTMLDivElement | null>(null);
    const reachedRef = useRef(reachedEnd);
    const onReachedRef = useRef(onReachedEnd);
    useEffect(() => {
        reachedRef.current = reachedEnd;
        onReachedRef.current = onReachedEnd;
    }, [reachedEnd, onReachedEnd]);

    useEffect(() => {
        if (!hasBody || reachedRef.current) return;
        const node = endRef.current;
        if (!node) return;

        // A browser with no IntersectionObserver cannot prove the end was reached, and this
        // gate must never be the reason a signature is impossible. The seconds floor still
        // applies, so the step keeps a gate; it keeps the weaker one.
        if (typeof IntersectionObserver === 'undefined') {
            onReachedRef.current();
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;
                observer.disconnect();
                if (reachedRef.current) return;
                reachedRef.current = true;
                onReachedRef.current();
            },
            { rootMargin: '0px 0px -8px 0px' },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasBody]);

    const html = useMemo(
        () => (pages ?? []).map((page) => sanitizeAgreementHtml(page)).join('\n'),
        [pages],
    );

    // ── Is the PDF actually filed? ────────────────────────────────────────────
    // Offered only on a 200. Anything else — a bucket with no object in it yet, a HEAD the
    // storage API declines, a network that is not there — hides the action rather than
    // shipping a button that lands the signer on an XML error.
    const [pdfFiled, setPdfFiled] = useState(false);
    useEffect(() => {
        setPdfFiled(false);
        if (!pdfUrl) return;
        let cancelled = false;
        void fetch(pdfUrl, { method: 'HEAD' })
            .then((response) => {
                if (!cancelled && response.ok) setPdfFiled(true);
            })
            .catch(() => {
                /* No copy to offer. The booklet on screen is the thing being accepted. */
            });
        return () => {
            cancelled = true;
        };
    }, [pdfUrl]);

    const remaining = Math.max(0, secondsFloor - tick);

    return (
        <>
            <div>
                <Eyebrow>The Terms this agreement incorporates</Eyebrow>
                <StepHeading>{title}</StepHeading>
                <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                    The Schedule you have just read is half of this agreement. These standing
                    Terms are the other half, and clause 3 of the Schedule incorporates this
                    exact version of them.
                </p>
            </div>

            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2 text-[11.5px] text-muted-foreground">
                <Lock className="h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 break-words">{citationLabel}</span>
            </div>

            {loading && (
                <div className="flex items-center justify-center gap-2 rounded-xl border border-border py-10 text-[12.5px] text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Opening the booklet…
                </div>
            )}

            {!loading && !hasBody && (
                <div className="rounded-xl border border-border p-4">
                    <div className="flex items-start gap-2.5">
                        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 space-y-2 text-[12.5px] leading-relaxed text-muted-foreground">
                            <p className="text-[13px] font-bold tracking-tight text-foreground">
                                Primelane could not open the booklet on this device
                            </p>
                            <p>
                                The version, form and checksum above identify exactly which
                                Terms this agreement incorporates, and they are printed inside
                                the document you are signing. Primelane records the whole of
                                that checksum and will give you the Terms and the rest of it
                                on request.
                            </p>
                            <p>
                                You can carry on and sign. Ask Primelane for a copy of these
                                Terms at any time.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {hasBody && (
                <>
                    <article
                        className="agreement-prose rounded-xl border border-border bg-card px-5 py-6 shadow-sm"
                        // Parsed by agreement-markdown.ts with html:false, then DOMPurified.
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                    {/* A ZERO-AREA TARGET IS NOT RELIABLY OBSERVED. An IntersectionObserver
                        watching a 0x0 element can go on reporting `isIntersecting: false`
                        forever, which here would be a gate that never opens and a signature
                        nobody can reach. One pixel of height, full width. */}
                    <div ref={endRef} className="h-px w-full" aria-hidden="true" />

                    <p className="text-center text-[11.5px] text-muted-foreground">
                        {!reachedEnd
                            ? 'Scroll to the end of the Terms to continue.'
                            : remaining > 0
                                ? `You have reached the end. ${remaining}s left of the minimum reading time.`
                                : `You have reached the end of the Terms.`}
                    </p>

                    {pdfFiled && pdfUrl && (
                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
                        >
                            <Download className="h-4 w-4" />
                            Download a copy of these Terms
                        </Button>
                    )}

                    <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                        A copy of these Terms accompanies the executed Schedule. Clause 3.3
                        records that they were shown to and accepted by the {partyNoun} in
                        this application before the Schedule was signed.
                    </p>
                </>
            )}
        </>
    );
}
